<template>
  <div>
    <CreateModal ref="modal" type="host">
      <div class="mw-700 py-5 mx-auto">
        <div class="d-flex justify-space-between align-center mb-5">
          <h3 class="mb-0">
            {{ $t('host.label.hostSpecialties') }}
          </h3>

          <div class="d-flex">
            <v-btn
              class="mr-2"
              color="error"
              elevation="0"
              @click.stop="openDeleteSpecialtyModal"
            >
              {{ $t('host.label.deleteSpecialties') }}
            </v-btn>

            <v-btn
              color="primary"
              elevation="0"
              @click.stop="openCreateSpecialtyModal"
            >
              {{ $t('host.label.createSpecialty') }}
            </v-btn>
          </div>
        </div>

        <v-autocomplete
          v-model="selectedHost"
          :items="hosts"
          :loading="isLoadingHosts"
          :search-input.sync="hostSearch"
          :item-text="hostItemText"
          item-value="id"
          label="Hosts"
          clearable
          outlined
          hide-selected
          return-object
          @change="onHostChange"
        />

        <v-autocomplete
          v-model="selectedSpecialtyIds"
          :items="specialties"
          :loading="isLoadingSpecialties"
          :search-input.sync="specialtySearch"
          item-text="name"
          item-value="id"
          multiple
          chips
          outlined
          clearable
          :disabled="!selectedHost"
          :no-data-text="$t('host.label.noSpecialties')"
          :label="$t('host.label.specialties')"
          :value-comparator="compareIds"
        />

        <div class="d-flex justify-end mt-4">
          <v-btn
            color="primary"
            elevation="0"
            :loading="isSaving"
            :disabled="!selectedHost"
            @click="save"
          >
            {{ $t('create.save') }}
          </v-btn>
        </div>
      </div>
    </CreateModal>

    <HostCreateSpecialtyModal />
    <HostDeleteSpecialtyModal />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateModal from '@/components/app/CreateModal'
import HostCreateSpecialtyModal from '@/components/host/modals/HostCreateSpecialtyModal'
import HostDeleteSpecialtyModal from '@/components/host/modals/HostDeleteSpecialtyModal'

export default {
  name: 'HostSpecialtiesModal',

  components: {
    CreateModal,
    HostCreateSpecialtyModal,
    HostDeleteSpecialtyModal,
  },

  data() {
    return {
      selectedHost: null,
      selectedSpecialtyIds: [],
      hostSearch: '',
      specialtySearch: '',
      isSaving: false,
    }
  },

  computed: {
    ...mapGetters([
      'hosts',
      'isLoading',
      'specialties',
      'hostSpecialties',
    ]),

    isLoadingHosts() {
      return this.isLoading && !this.selectedHost
    },

    isLoadingSpecialties() {
      return this.isLoading && !!this.selectedHost
    },
  },

  watch: {
    hostSearch(newValue) {
      this.fetchHosts({ search: newValue })
    },

    specialtySearch(newValue) {
      this.fetchSpecialties({ search: newValue })
    },
  },

  methods: {
    ...mapActions([
      'fetchHosts',
      'fetchSpecialties',
      'fetchHostSpecialties',
      'updateHostSpecialties',
      'showSuccessMessage',
      'showErrorMessage',
    ]),

    openModal() {
      this.selectedHost = null
      this.selectedSpecialtyIds = []
      this.hostSearch = ''
      this.specialtySearch = ''

      this.fetchHosts({ search: '' })
      this.fetchSpecialties({ search: '' })

      this.$refs.modal.openCreateModal()
    },

    openCreateSpecialtyModal() {
      this.$store.commit('openModal', 'CREATE_SPECIALTY_MODAL')
    },

    async openDeleteSpecialtyModal() {
      await this.fetchSpecialties({ search: '' })
      this.$store.commit('openModal', 'DELETE_SPECIALTY_MODAL')
    },

    hostItemText(host) {
      if (!host) return ''
      return `${host.name} - ${host.username}`
    },

    compareIds(a, b) {
      return String(a) === String(b)
    },

    async onHostChange(host) {
      if (!host) {
        this.selectedSpecialtyIds = []
        return
      }

      try {
        await this.fetchHostSpecialties(host.id)

        this.selectedSpecialtyIds = this.hostSpecialties.map((s) => {
          return s.specialtyId || s.specialty_id
        })
      } catch (e) {
        this.selectedSpecialtyIds = []
      }
    },

    async save() {
      if (!this.selectedHost) return

      this.isSaving = true

      try {
        await this.updateHostSpecialties({
          hostId: this.selectedHost.id,
          specialties: this.selectedSpecialtyIds,
        })

        this.showSuccessMessage(
          this.$t('host.label.specialtiesUpdated') ||
            'Especialidades atualizadas com sucesso!'
        )

        const modal = this.$refs.modal
        if (modal && typeof modal.closeCreateModal === 'function') {
          modal.closeCreateModal()
        }

        window.location.reload()
      } catch (error) {
        const message =
          error?.data?.message ||
          this.$t('host.label.specialtiesUpdateError') ||
          'Erro ao atualizar especialidades.'
        this.showErrorMessage(message)
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
